import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Tag, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_OPTIONS = [
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#10b981', label: 'Verde' },
  { value: '#f59e0b', label: 'Amarelo' },
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#14b8a6', label: 'Ciano' },
  { value: '#f97316', label: 'Laranja' },
];

const ICON_OPTIONS = [
  { value: 'tag', label: 'Tag' },
  { value: 'users', label: 'Usuários' },
  { value: 'briefcase', label: 'Pasta' },
  { value: 'star', label: 'Estrela' },
  { value: 'shield', label: 'Escudo' },
  { value: 'crown', label: 'Coroa' },
  { value: 'zap', label: 'Raio' },
  { value: 'heart', label: 'Coração' },
];

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#8b5cf6',
    icon: 'tag'
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      toast.error('Erro ao carregar grupos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingGroup) {
        await axios.put(`${API}/groups/${editingGroup.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Grupo atualizado!');
      } else {
        await axios.post(`${API}/groups`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Grupo criado!');
      }
      
      setOpen(false);
      setEditingGroup(null);
      setFormData({ name: '', description: '', color: '#8b5cf6', icon: 'tag' });
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar grupo');
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      color: group.color,
      icon: group.icon
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza? Clientes e templates deste grupo perderão a associação.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/groups/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Grupo excluído!');
      fetchGroups();
    } catch (error) {
      toast.error('Erro ao excluir grupo');
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingGroup(null);
    setFormData({ name: '', description: '', color: '#8b5cf6', icon: 'tag' });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-zinc-950">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
          <div className="text-white">Carregando...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-950">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Grupos</h1>
            <p className="text-slate-400">Organize clientes e templates por grupos</p>
          </div>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700">
                <Plus className="w-4 h-4 mr-2" />
                Novo Grupo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Ex: Plano Premium"
                    required
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Descrição opcional"
                  />
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">Cor</Label>
                  <div className="flex gap-2 flex-wrap">
                    {COLOR_OPTIONS.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData({...formData, color: color.value})}
                        className={`w-10 h-10 rounded-lg border-2 transition-all ${
                          formData.color === color.value ? 'border-white scale-110' : 'border-zinc-700'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">Ícone</Label>
                  <div className="flex gap-2 flex-wrap">
                    {ICON_OPTIONS.map(icon => (
                      <button
                        key={icon.value}
                        type="button"
                        onClick={() => setFormData({...formData, icon: icon.value})}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.icon === icon.value 
                            ? 'border-violet-500 bg-violet-600/20' 
                            : 'border-zinc-700 bg-zinc-800'
                        }`}
                        title={icon.label}
                      >
                        {icon.value === 'tag' && <Tag className="w-5 h-5 text-white" />}
                        {icon.value === 'users' && <Users className="w-5 h-5 text-white" />}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-violet-600 hover:bg-violet-700">
                    {editingGroup ? 'Atualizar' : 'Criar'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleCloseDialog}
                    className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">Nenhum grupo criado ainda</p>
            <Button onClick={() => setOpen(true)} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Grupo
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map(group => (
              <div
                key={group.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${group.color}20`, borderColor: group.color, borderWidth: '2px' }}
                    >
                      <Tag className="w-6 h-6" style={{ color: group.color }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{group.name}</h3>
                      {group.description && (
                        <p className="text-sm text-slate-400">{group.description}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(group)}
                    className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(group.id)}
                    className="flex-1 border-zinc-700 text-red-400 hover:bg-red-950"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Excluir
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
